<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <section class="page-hero no-print">
        <div class="container">
          <nav class="breadcrumb-nav">
            <a href="/" class="breadcrumb-link">Home</a>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">Verify Fire Safety Certificate</span>
          </nav>
          <h1 class="page-title">Verify Fire Safety Compliance Certificate</h1>
          <p class="page-subtitle">Confirm a Fire Safety certificate by Business ID or scan the QR on a printed certificate.</p>
        </div>
      </section>

      <!-- Search box (visible until a valid certificate loads) -->
      <section v-if="!valid && !loading" class="content-section no-print">
        <div class="container" style="max-width:720px;">
          <div class="verify-card">
            <label class="verify-label">Business ID</label>
            <div class="verify-row">
              <input type="text" class="verify-input" placeholder="e.g. 83365"
                     v-model="query" @keyup.enter="lookup" :disabled="loading">
              <button class="verify-btn" @click="lookup" :disabled="loading || !query.trim()">
                <span v-if="loading">Verifying…</span>
                <span v-else>Verify</span>
              </button>
            </div>
            <small class="verify-hint">Tip: scan the QR code on a printed certificate to verify it instantly.</small>
            <p v-if="notFoundMsg" class="verify-error">{{ notFoundMsg }}</p>
          </div>
        </div>
      </section>

      <!-- Verdict banner -->
      <section v-if="loaded" class="content-section no-print" style="padding-top:24px;padding-bottom:0;">
        <div class="container" style="max-width:920px;">
          <div class="verdict-banner" :class="valid ? 'ok' : 'bad'">
            <div class="verdict-icon">{{ valid ? '✓' : '!' }}</div>
            <div class="verdict-body">
              <h3>{{ valid ? 'Fire Safety Certificate is VALID' : 'Certificate NOT FOUND' }}</h3>
              <p v-if="valid">
                Business <strong>{{ permit.businessName || '—' }}</strong>
                <span v-if="permit.businessID"> · ID <strong>{{ permit.businessID }}</strong></span>
                <span v-if="permit.endDate"> · Expires <strong>{{ formatDate(permit.endDate) }}</strong></span>
              </p>
              <p v-else>
                We couldn't find a Fire Safety certificate for Business ID <code>{{ lastQueried }}</code>. Check the ID and try again.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Floating Print FAB -->
      <button v-if="valid" @click="printCert" class="print-fab no-print" type="button">
        <span aria-hidden="true">🖨</span>
        <span class="print-fab-label">Print / Save as PDF</span>
      </button>

      <!-- Certificate render (visual + print target) -->
      <section v-if="valid" class="cert-section">
        <div class="cert-outer">
          <div class="page-container">

            <img v-if="qrLink" :src="qrLink" alt="qr"
                 style="position:absolute;top:0.55in;left:0.55in;width:1.1in;height:1.1in;">

            <div style="position:absolute;top:0.55in;right:0.55in;text-align:right;">
              <div style="font:700 15pt Aldrich,sans-serif;color:#000;letter-spacing:1px;">
                Serial NO. {{ permit.businessID || '—' }}
              </div>
              <div style="margin-top:6px;width:22px;height:22px;border:1.5px solid #000;display:inline-block;"></div>
            </div>

            <div style="position:absolute;top:1.7in;left:0;right:0;text-align:center;">
              <img v-if="county.logo" :src="county.logo" alt="county-coat"
                   style="height:1.55in;max-width:2in;object-fit:contain;">
            </div>

            <div style="position:absolute;top:3.5in;left:0.6in;right:0.6in;text-align:center;">
              <div style="font:800 22pt Montserrat,sans-serif;color:#111;text-transform:uppercase;letter-spacing:0.5px;line-height:1.15;">
                County Government of {{ (county.countyName || '').toUpperCase() }}
              </div>
              <div style="font:700 12pt Montserrat,sans-serif;color:#111;text-transform:uppercase;margin-top:6px;letter-spacing:0.5px;">
                Department of Fire &amp; Rescue Services
              </div>
            </div>

            <!-- Cursive title (nowrap so it never wraps into paragraph). -->
            <div style="position:absolute;top:4.7in;left:0.6in;right:0.6in;text-align:center;">
              <span style="font:400 36pt 'Fondamento',cursive;color:#111;white-space:nowrap;display:inline-block;">Fire Safety Compliance Certificate</span>
            </div>

            <div style="position:absolute;top:5.9in;left:0.7in;right:0.7in;text-align:center;
                        font:400 12pt 'Fira Sans',sans-serif;color:#111;line-height:1.55;">
              This is to certify that
              <strong>{{ permit.businessName || '—' }}</strong>
              (Business ID <strong>{{ permit.businessID || '—' }}</strong>)
              has met the fire-safety requirements for premises at
              <strong>{{ permit.physicalAddress || permit.buildingName || '—' }}</strong>.
            </div>

            <div style="position:absolute;top:7.5in;left:0.6in;right:0.6in;font:400 11pt 'Fira Sans',sans-serif;color:#111;">
              <div><strong>Business Category:</strong> {{ permit.businessCategory || '—' }}</div>
              <div><strong>Sub County:</strong> {{ permit.subCountyName || '—' }}</div>
              <div><strong>Ward:</strong> {{ permit.wardName || '—' }}</div>
              <div v-if="permit.startDate"><strong>Issued On:</strong> {{ formatDate(permit.startDate) }}</div>
              <div v-if="permit.endDate"><strong>Valid Until:</strong> {{ formatDate(permit.endDate) }}</div>
            </div>

            <!-- Fire Engineer signature block. Uploaded via /fire-settings
                 (module='fire', assetKey='fireSignature'). Falls back to
                 county-level signatureFire so counties that haven't set
                 the module-level one still show something. Dotted line
                 last-resort so the block never renders empty. -->
            <div style="position:absolute;bottom:1.6in;left:0.6in;width:3in;
                        font:400 11pt 'Fira Sans',sans-serif;color:#111;text-align:center;">
              <img v-if="signatureUrl" :src="signatureUrl" alt="signature"
                   style="height:0.55in;width:auto;object-fit:contain;">
              <span v-else style="display:inline-block;border-top:1px dotted #666;width:2.4in;height:0.55in;">&nbsp;</span>
              <hr style="margin:2mm 0;">
              <div>By Order Of Fire Engineer</div>
            </div>

            <div style="position:absolute;bottom:1.05in;right:1.1in;text-align:center;">
              <img v-if="rubberStampUrl || sealFire" :src="rubberStampUrl || sealFire" alt="seal"
                   style="height:1.35in;width:auto;">
              <div style="margin-top:4px;background:#c9d5e6;color:#1c4a94;font:700 10pt 'Fira Sans',sans-serif;
                          padding:4px 12px;letter-spacing:2px;border-radius:3px;display:inline-block;">
                APPROVED
              </div>
            </div>

            <img v-if="poweredByLogo" :src="poweredByLogo" alt="powered"
                 style="position:absolute;bottom:1.15in;left:1.1in;height:0.5in;">

            <div style="position:absolute;bottom:0.7in;left:1.1in;right:1.1in;
                        font:400 10pt 'Fira Sans',sans-serif;color:#111;">
              <strong>NB:</strong> This certificate does not exempt the holder from obeying existing laws.
            </div>

            <div style="position:absolute;bottom:0.35in;left:1.1in;right:1.1in;
                        display:flex;justify-content:space-between;gap:0.5in;
                        font:400 8pt 'Fira Sans',sans-serif;color:#111;">
              <span>
                <strong>County Government of {{ county.countyName }},</strong>
                | {{ county.address || 'P.O Box —, ' + (county.countyName || '') }}
              </span>
              <span>
                <strong>Contacts, T:</strong> {{ county.contact || '—' }}
                <strong> E:</strong> {{ county.email || '—' }}
              </span>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent class="no-print" />
  </div>
</template>

<script>
/* eslint-disable */
// Public Fire Safety Certificate verification / print page. Hit by the
// QR on every printed FirePermit (RMS-MAIN FirePermit.vue encodes
// <selfService>/#/verify-fire/<businessID>). Renders the certificate
// with a validity banner + Print / Save-as-PDF button.
import { execute, fire, biller, base } from '@/api';
import HeaderComponent from '@/components/HeaderComponent.vue';
import FooterComponent from '@/components/FooterComponent.vue';

export default {
  name: 'VerifyFire',
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      query: '',
      lastQueried: '',
      loading: false,
      loaded: false,
      valid: false,
      notFoundMsg: '',

      sealFire:       base + '/logo/signature-fire.png',
      poweredByLogo:  base + '/logo/poweredbylogo.png',
      rubberStampUrl: '',
      // Fire Engineer signature — uploaded via /fire-settings on RMS-MAIN
      // (assetKey='fireSignature'). Falls back to county.signatureFire if
      // not set; last-resort dotted line so the block never renders empty.
      signatureUrl:   '',
      qrLink: '',

      permit: {},
      county: {},
    };
  },
  mounted() {
    this.loadRubberStamp();
    const fromParam = this.$route && this.$route.params && this.$route.params.businessID;
    if (fromParam) {
      this.query = decodeURIComponent(fromParam);
      this.lookup();
    }
  },
  methods: {
    printCert() { window.print(); },
    formatDate(s) {
      if (!s) return '—';
      const d = new Date(String(s).replace(' ', 'T'));
      if (isNaN(d.getTime())) return s;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${('0'+d.getDate()).slice(-2)} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },
    loadRubberStamp() {
      try {
        const fd = new FormData();
        fd.append('function', 'getModuleSettings');
        fd.append('module', 'fire');
        execute(fd, biller).then(res => {
          const d = res && res.data && res.data.success && res.data.data ? res.data.data : {};
          if (d.rubberStampUrl)   this.rubberStampUrl = d.rubberStampUrl   + '?v=' + Date.now();
          if (d.fireSignatureUrl) this.signatureUrl   = d.fireSignatureUrl + '?v=' + Date.now();
        }).catch(() => {});
      } catch (e) { /* ignore */ }
    },
    lookup() {
      const id = String(this.query || '').trim();
      if (!id) return;
      this.notFoundMsg = '';
      this.loading = true;
      const fd = new FormData();
      fd.append('function', 'printFirePermit');
      fd.append('businessID', id);
      execute(fd, fire).then(res => {
        this.loading = false;
        this.loaded  = true;
        this.lastQueried = id;
        if (res && res.data && res.data.success && res.data.data && res.data.data.permit) {
          this.valid   = true;
          this.permit  = res.data.data.permit || {};
          this.county  = res.data.data.county || {};
          if (this.county.poweredByLogo) this.poweredByLogo = this.county.poweredByLogo;
          // County-level signatureFire is the fallback when no per-module
          // Fire Engineer signature has been uploaded via /fire-settings.
          if (!this.signatureUrl && this.county.signatureFire) {
            this.signatureUrl = this.county.signatureFire;
          }
          const selfSvc = this.county.selfService ? String(this.county.selfService).replace(/\/$/, '') : '';
          const key = this.permit.businessID || id;
          if (selfSvc && key) {
            this.qrLink = 'https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=' +
                          encodeURIComponent(selfSvc + '/#/verify-fire/' + encodeURIComponent(key));
          }
        } else {
          this.valid   = false;
          this.permit  = {};
          this.notFoundMsg = (res && res.data && res.data.message) || 'Fire certificate not found';
        }
      }).catch(() => {
        this.loading = false;
        this.loaded  = true;
        this.valid   = false;
        this.notFoundMsg = 'Network error — please retry.';
      });
    },
  },
};
</script>

<style scoped>
.page-wrapper { min-height: 100vh; display: flex; flex-direction: column; background: #f8fafc; }
.main-content { flex: 1; padding-top: 72px; }
.page-hero { background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); padding: 48px 0; color: #fff; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
.breadcrumb-nav { display:flex; gap:8px; margin-bottom:16px; font-size:14px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration:none; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color:#fff; }
.page-title { font-size: 30px; font-weight: 800; margin-bottom: 6px; }
.page-subtitle { opacity: 0.9; font-size: 15px; }

.content-section { padding: 40px 0; }

.verify-card { background:#fff; border-radius:16px; padding:24px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
.verify-label { display:block; font-weight:600; color:#1e293b; margin-bottom:10px; }
.verify-row { display:flex; gap:10px; }
.verify-input { flex:1; padding:12px 14px; border:2px solid #e2e8f0; border-radius:10px; font-size:15px; }
.verify-btn { padding:12px 20px; background:#0f4c81; color:#fff; border:0; border-radius:10px; font-weight:600; cursor:pointer; }
.verify-btn:disabled { opacity:0.6; cursor:not-allowed; }
.verify-hint { display:block; margin-top:10px; color:#64748b; }
.verify-error { color:#dc2626; margin-top:12px; font-weight:500; }

.verdict-banner { display:flex; gap:16px; align-items:center; padding:20px 24px; border-radius:14px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
.verdict-banner.ok  { background:#d1fae5; }
.verdict-banner.bad { background:#fee2e2; }
.verdict-icon { width:52px; height:52px; border-radius:50%; background:#fff; display:flex; align-items:center; justify-content:center; font-size:28px; font-weight:700; flex-shrink:0; }
.verdict-banner.ok  .verdict-icon { color:#059669; }
.verdict-banner.bad .verdict-icon { color:#dc2626; }
.verdict-body h3 { margin:0 0 4px 0; font-size:18px; font-weight:700; color:#1e293b; }
.verdict-body p  { margin:0; font-size:14px; color:#334155; }

.print-fab {
  position: fixed; top: 88px; right: 24px; z-index: 1050;
  display: inline-flex; align-items: center; gap: 8px;
  background: #0f4c81; color: #fff; border: 0; border-radius: 999px;
  padding: 12px 20px; font: 600 14px/1.2 var(--font-body, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
  cursor: pointer; box-shadow: 0 6px 20px rgba(15,76,129,0.35); white-space: nowrap;
}
.print-fab:hover { background:#0a3459; }
@media (max-width: 480px) { .print-fab { padding: 12px 14px; } .print-fab-label { display: none; } }

.cert-section { padding: 24px 0 60px; background:#eaeaea; }
.cert-outer   { background:#eaeaea; }
.page-container {
  width:8.268in; height:11.693in; position:relative;
  margin:0 auto; padding:0; background:#fff;
  background-size:100% 100%; background-repeat:no-repeat;
}

@media print {
  .no-print { display: none !important; }
  .main-content { padding-top: 0 !important; }
  .cert-section { background: #fff !important; padding: 0 !important; }
  .cert-outer   { background: #fff !important; }
  .page-wrapper { background: #fff !important; }
}
</style>
